'use client';

import React, { useState, useEffect } from 'react';
import { X, AlertTriangle, CheckCircle, Skull, TrendingDown } from 'lucide-react';
import {
  FLOCK_OPTIONS,
  SUSPECTED_CAUSES,
  CAUSE_CATEGORIES,
  DISPOSAL_METHODS,
  THRESHOLD_CONFIGS,
  FlockOption,
} from './mortalityData';

interface Props {
  onClose: () => void;
  onSave: (data: Record<string, unknown>) => void;
}

interface FormState {
  date: string;
  flockId: string;
  numberDead: string;
  numberCulled: string;
  suspectedCause: string;
  confirmedCause: string;
  causeCategory: string;
  disposalMethod: string;
  notes: string;
  recordedBy: string;
}

const INITIAL: FormState = {
  date: new Date().toISOString().split('T')[0],
  flockId: '',
  numberDead: '',
  numberCulled: '',
  suspectedCause: '',
  confirmedCause: '',
  causeCategory: '',
  disposalMethod: 'Deep Burial',
  notes: '',
  recordedBy: '',
};

export default function MortalityRecordForm({ onClose, onSave }: Props) {
  const [form, setForm] = useState<FormState>(INITIAL);
  const [selectedFlock, setSelectedFlock] = useState<FlockOption | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});

  // Derived values
  const dead = parseInt(form.numberDead) || 0;
  const culled = parseInt(form.numberCulled) || 0;
  const totalRemoved = dead + culled;
  const closingCount = selectedFlock ? Math.max(0, selectedFlock.currentQty - totalRemoved) : null;
  const dailyMortalityPct = selectedFlock && dead > 0
    ? ((dead / selectedFlock.currentQty) * 100)
    : 0;

  // Threshold check
  const thresholdConfig = selectedFlock
    ? THRESHOLD_CONFIGS.find(t => t.flockId === selectedFlock.id)
    : null;
  const thresholdBreached = thresholdConfig
    ? dailyMortalityPct > thresholdConfig.dailyThresholdPct
    : false;

  useEffect(() => {
    if (form.flockId) {
      const flock = FLOCK_OPTIONS.find(f => f.id === form.flockId) ?? null;
      setSelectedFlock(flock);
    } else {
      setSelectedFlock(null);
    }
  }, [form.flockId]);

  function set(field: keyof FormState, value: string) {
    setForm(f => ({ ...f, [field]: value }));
    if (errors[field]) setErrors(e => ({ ...e, [field]: undefined }));
  }

  function validate(): boolean {
    const e: Partial<Record<keyof FormState, string>> = {};
    if (!form.date) e.date = 'Date is required';
    if (!form.flockId) e.flockId = 'Flock is required';
    if (!form.numberDead && !form.numberCulled) e.numberDead = 'Enter at least deaths or culled count';
    if (form.numberDead && parseInt(form.numberDead) < 0) e.numberDead = 'Cannot be negative';
    if (form.numberCulled && parseInt(form.numberCulled) < 0) e.numberCulled = 'Cannot be negative';
    if (selectedFlock && totalRemoved > selectedFlock.currentQty) e.numberDead = 'Total removed exceeds current flock count';
    if (!form.suspectedCause) e.suspectedCause = 'Suspected cause is required';
    if (!form.disposalMethod) e.disposalMethod = 'Disposal method is required';
    if (!form.recordedBy) e.recordedBy = 'Recorded by is required';
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function handleSave() {
    if (!validate()) return;
    setSaving(true);
    await new Promise(r => setTimeout(r, 800));
    setSaving(false);
    setSaved(true);
    onSave({
      ...form,
      numberDead: dead,
      numberCulled: culled,
      closingCount,
      dailyMortalityPct,
      thresholdBreached,
      flockNumber: selectedFlock?.flockNumber,
      flockName: selectedFlock?.flockName,
      farm: selectedFlock?.farm,
      house: selectedFlock?.house,
    });
    setTimeout(onClose, 1200);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 sticky top-0 bg-white z-10">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-red-100 flex items-center justify-center">
              <Skull size={16} className="text-red-600" />
            </div>
            <div>
              <h2 className="text-sm font-bold text-gray-900">New Mortality / Culling Record</h2>
              <p className="text-xs text-gray-400">Flock population will be automatically reduced on save</p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X size={18} />
          </button>
        </div>

        {/* Threshold Alert */}
        {thresholdBreached && (
          <div className="mx-6 mt-4 flex items-start gap-3 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
            <AlertTriangle size={16} className="text-red-600 flex-shrink-0 mt-0.5" />
            <div className="text-xs">
              <p className="font-semibold text-red-800">Daily Mortality Threshold Exceeded</p>
              <p className="text-red-600 mt-0.5">
                Current entry: <strong>{dailyMortalityPct.toFixed(3)}%</strong> — Threshold: <strong>{thresholdConfig?.dailyThresholdPct}%</strong>.
                An alert will be generated and the farm manager will be notified.
              </p>
            </div>
          </div>
        )}

        {/* Population Preview */}
        {selectedFlock && (
          <div className="mx-6 mt-4 grid grid-cols-3 gap-3">
            <div className="bg-gray-50 rounded-xl p-3 text-center">
              <p className="text-xs text-gray-400">Opening Count</p>
              <p className="text-lg font-bold text-gray-800">{selectedFlock.currentQty.toLocaleString()}</p>
            </div>
            <div className="bg-red-50 rounded-xl p-3 text-center">
              <p className="text-xs text-red-400">Total Removed</p>
              <p className="text-lg font-bold text-red-700">−{totalRemoved.toLocaleString()}</p>
              <p className="text-xs text-red-400">{dead} dead + {culled} culled</p>
            </div>
            <div className="bg-green-50 rounded-xl p-3 text-center">
              <p className="text-xs text-green-500">New Closing Count</p>
              <p className="text-lg font-bold text-green-700">{closingCount?.toLocaleString() ?? '—'}</p>
            </div>
          </div>
        )}

        {/* Form Body */}
        <div className="px-6 py-5 space-y-5">
          {/* Row 1: Date + Flock */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">Date <span className="text-red-500">*</span></label>
              <input
                type="date"
                value={form.date}
                onChange={e => set('date', e.target.value)}
                className={`w-full text-sm border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500 ${errors.date ? 'border-red-400' : 'border-gray-200'}`}
              />
              {errors.date && <p className="text-xs text-red-500 mt-1">{errors.date}</p>}
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">Flock <span className="text-red-500">*</span></label>
              <select
                value={form.flockId}
                onChange={e => set('flockId', e.target.value)}
                className={`w-full text-sm border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500 bg-white ${errors.flockId ? 'border-red-400' : 'border-gray-200'}`}
              >
                <option value="">Select flock...</option>
                {FLOCK_OPTIONS.map(f => (
                  <option key={f.id} value={f.id}>
                    {f.flockNumber} — {f.flockName} ({f.currentQty.toLocaleString()} birds)
                  </option>
                ))}
              </select>
              {errors.flockId && <p className="text-xs text-red-500 mt-1">{errors.flockId}</p>}
              {selectedFlock && (
                <p className="text-xs text-gray-400 mt-1">{selectedFlock.house} · {selectedFlock.farm} · Age: {selectedFlock.ageDays} days</p>
              )}
            </div>
          </div>

          {/* Row 2: Deaths + Culled */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                <span className="flex items-center gap-1"><Skull size={12} className="text-red-500" /> Number Dead</span>
              </label>
              <input
                type="number"
                min="0"
                placeholder="0"
                value={form.numberDead}
                onChange={e => set('numberDead', e.target.value)}
                className={`w-full text-sm border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500 ${errors.numberDead ? 'border-red-400' : 'border-gray-200'}`}
              />
              {errors.numberDead && <p className="text-xs text-red-500 mt-1">{errors.numberDead}</p>}
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                <span className="flex items-center gap-1"><TrendingDown size={12} className="text-amber-500" /> Number Culled</span>
              </label>
              <input
                type="number"
                min="0"
                placeholder="0"
                value={form.numberCulled}
                onChange={e => set('numberCulled', e.target.value)}
                className={`w-full text-sm border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500 ${errors.numberCulled ? 'border-red-400' : 'border-gray-200'}`}
              />
            </div>
          </div>

          {/* Row 3: Suspected Cause + Category */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">Suspected Cause <span className="text-red-500">*</span></label>
              <select
                value={form.suspectedCause}
                onChange={e => set('suspectedCause', e.target.value)}
                className={`w-full text-sm border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500 bg-white ${errors.suspectedCause ? 'border-red-400' : 'border-gray-200'}`}
              >
                <option value="">Select cause...</option>
                {SUSPECTED_CAUSES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              {errors.suspectedCause && <p className="text-xs text-red-500 mt-1">{errors.suspectedCause}</p>}
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">Cause Category</label>
              <select
                value={form.causeCategory}
                onChange={e => set('causeCategory', e.target.value)}
                className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500 bg-white"
              >
                <option value="">Select category...</option>
                {CAUSE_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>

          {/* Row 4: Confirmed Cause + Disposal */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">Confirmed Cause <span className="text-gray-400 font-normal">(optional)</span></label>
              <input
                type="text"
                placeholder="Lab-confirmed diagnosis..."
                value={form.confirmedCause}
                onChange={e => set('confirmedCause', e.target.value)}
                className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">Disposal Method <span className="text-red-500">*</span></label>
              <select
                value={form.disposalMethod}
                onChange={e => set('disposalMethod', e.target.value)}
                className={`w-full text-sm border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500 bg-white ${errors.disposalMethod ? 'border-red-400' : 'border-gray-200'}`}
              >
                {DISPOSAL_METHODS.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Notes / Observations</label>
            <textarea
              rows={3}
              placeholder="Describe symptoms, circumstances, actions taken..."
              value={form.notes}
              onChange={e => set('notes', e.target.value)}
              className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500 resize-none"
            />
          </div>

          {/* Recorded By */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Recorded By <span className="text-red-500">*</span></label>
            <input
              type="text"
              placeholder="Farm worker / manager name..."
              value={form.recordedBy}
              onChange={e => set('recordedBy', e.target.value)}
              className={`w-full text-sm border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500 ${errors.recordedBy ? 'border-red-400' : 'border-gray-200'}`}
            />
            {errors.recordedBy && <p className="text-xs text-red-500 mt-1">{errors.recordedBy}</p>}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 bg-gray-50 rounded-b-2xl sticky bottom-0">
          <p className="text-xs text-gray-400">
            {selectedFlock && totalRemoved > 0
              ? `Flock population will reduce by ${totalRemoved} bird${totalRemoved !== 1 ? 's' : ''} on save`
              : 'Population will auto-update when saved'}
          </p>
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving || saved}
              className="flex items-center gap-2 px-5 py-2 text-sm font-semibold text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-60 transition-colors"
            >
              {saved ? (
                <><CheckCircle size={14} /> Saved</>
              ) : saving ? (
                <><span className="w-3 h-3 border-2 border-white/40 border-t-white rounded-full animate-spin" /> Saving...</>
              ) : (
                <><Skull size={14} /> Save Record</>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
